import type { MidiCCEvent, MidiNoteEvent, UserNotification } from "@/client/core/Core.api";
import { showErrorFactory, showError } from "@/client/utils/error-handling";
import type { Signal } from "@/client/utils/signal";

export const enum MidiEvent {
    NoteOff = 0x80,
    NoteOn = 0x90,
    ControlChange = 0xb0,
}

const kMidiStatusMask = 0xf0;

export const handleMidiMessage = (
    event: MIDIMessageEvent,
    midiNoteSignal: Signal<MidiNoteEvent>,
    midiControlChangeSignal: Signal<MidiCCEvent>,
): void => {
    if (event.data === null || event.data.length < 3) {
        return;
    }
    const status = (event.data[0] & kMidiStatusMask) as MidiEvent;

    switch (status) {
        case MidiEvent.NoteOn:
        case MidiEvent.NoteOff: {
            const noteNumber = event.data[1];
            const velocity = status === MidiEvent.NoteOff ? 0 : event.data[2];
            midiNoteSignal.notify({ noteNumber, velocity });
            break;
        }
        case MidiEvent.ControlChange: {
            midiControlChangeSignal.notify({ ccNumber: event.data[1], value: event.data[2] });
            break;
        }
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        default:
            // eslint-disable-next-line no-console
            console.warn("Unhandled MIDI event type: ", status);
            break;
    }
};

export const createWebsiteMidiService = (
    midiNoteSignal: Signal<MidiNoteEvent>,
    midiControlChangeSignal: Signal<MidiCCEvent>,
    notificationSignal: Signal<Readonly<UserNotification>>,
) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- safari does not support requestMIDIAccess
    if (navigator.requestMIDIAccess === undefined) {
        showError(
            notificationSignal.notify,
            "Web MIDI",
            "Web MIDI is not supported in this browser.",
        );
        return;
    }
    navigator.requestMIDIAccess().then(
        (access) => {
            const listener = (event: MIDIMessageEvent) => {
                handleMidiMessage(event, midiNoteSignal, midiControlChangeSignal);
            };
            for (const input of access.inputs.values()) {
                input.addEventListener("midimessage", listener);
            }
            access.addEventListener("statechange", () => {
                for (const input of access.inputs.values()) {
                    input.removeEventListener("midimessage", listener);
                    input.addEventListener("midimessage", listener);
                }
            });
        },
        showErrorFactory(notificationSignal.notify, "Web MIDI"),
    );
};
